import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, Layers, Camera } from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import { api } from '@/lib/api/client';
import LogoAvatar from '@/components/LogoAvatar';

interface CollectedLogo {
  id: string;
  tenantName: string;
  logoUrl: string | null;
  buildingName: string;
  collectedAt: string;
}

export default function CollectionPage() {
  const { t } = useTranslation('scout');
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthStore();
  const [collection, setCollection] = useState<CollectedLogo[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { replace: true });
      return;
    }
    loadCollection();
  }, [isAuthenticated, navigate]);

  async function loadCollection() {
    try {
      setLoadError(false);
      const data = await api.get<CollectedLogo[]>('/scout/collection');
      setCollection(data);
    } catch {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-primary/5 to-white flex flex-col items-center justify-center px-6">
        <Layers className="w-12 h-12 text-steel mb-3" />
        <p className="text-small text-muted-foreground text-center mb-4">{t('collection.loadError')}</p>
        <button
          onClick={() => {
            setLoading(true);
            loadCollection();
          }}
          className="px-5 py-2.5 rounded-xl bg-muted text-small font-medium text-foreground"
        >
          {t('collection.retry')}
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-primary/5 to-white pb-24">
      {/* Header */}
      <div className="px-4 pt-6 pb-4 flex items-center gap-3">
        <button onClick={() => navigate('/')} className="p-2 -ms-2 rounded-lg hover:bg-warm-faint">
          <ChevronLeft className="w-5 h-5 text-muted-foreground" />
        </button>
        <div>
          <h1 className="text-section font-bold text-foreground">{t('collection.title')}</h1>
          <p className="text-small text-muted-foreground">{t('collection.subtitle', { count: collection.length })}</p>
        </div>
      </div>

      {collection.length === 0 ? (
        <div className="text-center py-12 px-6">
          <Layers className="w-12 h-12 text-steel mx-auto mb-3" />
          <p className="text-muted-foreground mb-4">{t('collection.empty')}</p>
          <button
            onClick={() => navigate('/scan')}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-primary to-forest text-white font-medium text-small active:scale-[0.98] transition-all"
          >
            <Camera className="w-4 h-4" />
            {t('common.openScanner')}
          </button>
        </div>
      ) : (
        <div className="px-4 grid grid-cols-3 gap-3">
          {collection.map((item) => (
            <LogoCard key={item.id} item={item} t={t} />
          ))}
        </div>
      )}
    </div>
  );
}

function LogoCard({ item, t }: { item: CollectedLogo; t: (key: string, opts?: Record<string, unknown>) => string }) {
  return (
    <div className="p-3 rounded-xl bg-white border border-primary/10 shadow-sm text-center">
      {/* Logo */}
      <LogoAvatar
        logoUrl={item.logoUrl}
        tenantName={item.tenantName}
        size="w-14 h-14"
        initialsSize="text-lg"
        className="mx-auto mb-2 bg-gradient-to-br from-primary/10 to-primary/5 ring-2 ring-primary/20"
      />

      {/* Tenant name */}
      <p className="text-caption font-semibold text-foreground leading-tight truncate">{item.tenantName}</p>

      {/* First visit building */}
      <p className="text-[10px] text-muted-foreground mt-0.5 truncate">
        {t('collection.firstVisit', { building: item.buildingName })}
      </p>
    </div>
  );
}
